import { GuestState } from "@/types/enums/GuestState";
import {
  isRenderableOptionColor,
  isValidOptionColor,
} from "@/lib/utils/optionChipColor";
import { CHART_COLORS } from "./chartColors";

// Default color per guest state (raw enum value -> CSS color). Routed through
// the semantic --color-status-* tokens so the guest donut, dashboard legend
// dots and the event-grid funnel use the EXACT same hues as the email/seating/
// slotting status charts — and adapt to dark mode. Hues stay in the same
// family as the guestlist status chips (TCCGuestStatus.tsx): uncontacted=amber,
// contacted=blue, accepted=green, declined=red, waitlisted=rose.
export const GUEST_STATE_COLORS: Record<string, string> = {
  [GuestState.UNCONTACTED]: "var(--color-status-warning)",
  [GuestState.CONTACTED]: "var(--color-status-info)",
  [GuestState.ACCEPTED]: "var(--color-status-success)",
  [GuestState.DECLINED]: "var(--color-status-danger)",
  [GuestState.WAITLISTED]: "var(--color-status-hold)",
};

// Effective DEFAULT colors for a charted field's options, in the same priority
// the chart itself renders: semantic status map (guest state) > positional
// categorical ramp. Used to pre-fill the color picker so users SEE the current
// default and can override it (instead of an empty swatch).
export function defaultChartColors(
  options: string[],
  fieldName?: string,
): Record<string, string> {
  const statusMap = fieldName === "state" ? GUEST_STATE_COLORS : undefined;
  const result: Record<string, string> = {};
  options.forEach((opt, i) => {
    result[opt] = statusMap?.[opt] ?? CHART_COLORS[i % CHART_COLORS.length];
  });
  return result;
}

/**
 * Reorders chart buckets into the field's option order so bars/slices line up
 * with the guestlist editor: known options first in definition order, stale/
 * unknown keys after in their incoming (backend) order, the not-specified
 * bucket last. Returns the input unchanged when there is nothing to reorder.
 */
export function orderBucketsByOptions<T>(
  data: Record<string, T>,
  options: string[],
  notSpecifiedLabel: string,
): Record<string, T> {
  const ordered: Record<string, T> = {};
  for (const opt of options) {
    if (opt in data) ordered[opt] = data[opt];
  }
  for (const key of Object.keys(data)) {
    if (!(key in ordered) && key !== notSpecifiedLabel) ordered[key] = data[key];
  }
  if (notSpecifiedLabel in data && !(notSpecifiedLabel in ordered)) {
    ordered[notSpecifiedLabel] = data[notSpecifiedLabel];
  }
  return ordered;
}

type BuildArgs = {
  /** Raw category keys (raw enum values, or raw SELECT option labels). */
  rawKeys: string[];
  /**
   * Maps a raw key to the label that actually appears in the chart `values`:
   * translated for enum fields (`t(rawKey)`), identity for custom SELECT fields.
   */
  labelFor: (rawKey: string) => string;
  /**
   * Per-option colors from a charted SELECT field (label -> hex), if any —
   * optionally with default-palette var() tokens pre-merged underneath.
   */
  optionColors?: Record<string, string>;
  /** Semantic status map keyed by raw key, when this is a known status field. */
  statusMap?: Record<string, string>;
  /** Label of the collapsed empty/unknown bucket -> neutral (or override). */
  notSpecifiedLabel?: string;
  /** Per-card custom color for the not-specified bucket (hex); default neutral. */
  notSpecifiedColor?: string;
};

/**
 * Builds a `label -> color` map for the categories we can resolve. Priority per
 * key: renderable `optionColors` (explicit hex, or a pre-merged default-palette
 * `var()` — see the TCC*Select idiom mirrored in CardComponent) > semantic
 * `statusMap` > omit. Omitted keys fall back to the positional CHART_COLORS
 * ramp inside the chart component.
 *
 * IMPORTANT: keys are the FINAL display labels (post-translation) so they match
 * the already-translated `values` keys produced in CardComponent.
 */
export function buildChartColors({
  rawKeys,
  labelFor,
  optionColors,
  statusMap,
  notSpecifiedLabel,
  notSpecifiedColor,
}: BuildArgs): Record<string, string> {
  const colors: Record<string, string> = {};
  for (const rawKey of rawKeys) {
    const label = labelFor(rawKey);
    const optionColor = optionColors?.[rawKey];
    // Renderable (not the strict persistence check): the map may carry
    // default-palette var() tokens merged under the stored hex colors.
    if (isRenderableOptionColor(optionColor)) {
      colors[label] = optionColor;
    } else if (statusMap?.[rawKey]) {
      colors[label] = statusMap[rawKey];
    }
  }
  if (notSpecifiedLabel) {
    colors[notSpecifiedLabel] = isValidOptionColor(notSpecifiedColor)
      ? notSpecifiedColor
      : "var(--color-chart-neutral)";
  }
  return colors;
}
